import type { RepositoryAccessConfig } from "@p0security/integrations-github-shared";
import { ValidationError } from "@p0security/shared/types/error";
import { assertNeverError } from "@p0security/shared/types/index";

import {
  type ConnectorArgs,
  type ConnectorContext,
  renderAwsTerraform,
  renderGcpTerraform,
  toAwsConnector,
  toGcpConnector,
} from "./iac.ts";
import { renderAwsShell, renderGcpShell } from "./shell.ts";

/** The connector's Terraform and shell script, or the input they can't be
 * rendered with. */
export type RenderedCommands =
  | { error: ValidationError; shell?: never; terraform?: never }
  | { error?: never; shell: string; terraform: string };

/** The fields of an item that its connector's commands are rendered from. */
type ConnectorFields = Pick<
  RepositoryAccessConfig,
  "hosting" | "privateKeySecretName" | "vault"
>;

/** Renders one cloud's connector Terraform and shell script. */
const renderCommands = (args: ConnectorArgs) => {
  switch (args.cloud) {
    case "aws": {
      const connector = toAwsConnector(args);
      return {
        shell: renderAwsShell(connector),
        terraform: renderAwsTerraform(connector),
      };
    }
    case "gcp": {
      const connector = toGcpConnector(args);
      return {
        shell: renderGcpShell(connector),
        terraform: renderGcpTerraform(connector),
      };
    }
    default:
      throw assertNeverError(args);
  }
};

/** Renders the connector's Terraform and shell script, catching the
 * {@link ValidationError} for the first input they can't be rendered with. */
const toRenderedCommands = (args: ConnectorArgs): RenderedCommands => {
  try {
    return renderCommands(args);
  } catch (error) {
    if (error instanceof ValidationError) return { error };
    throw error;
  }
};

/**
 * Renders the Terraform and the shell script for an install item's connector,
 * from its stored fields, or returns `undefined` when its vault and connector
 * are in different clouds. P0 refuses to store such an item, so only a
 * hand-edited one can be.
 */
export const toItemCommands = (
  context: ConnectorContext,
  org: string,
  { hosting, privateKeySecretName, vault }: ConnectorFields
): RenderedCommands | undefined =>
  hosting.type === "aws" && vault.type === "aws-sm"
    ? toRenderedCommands({
        cloud: "aws",
        context,
        inputs: { hosting, privateKeySecretName, vault },
        org,
      })
    : hosting.type === "gcp" && vault.type === "gcp-sm"
      ? toRenderedCommands({
          cloud: "gcp",
          context,
          inputs: { hosting, privateKeySecretName, vault },
          org,
        })
      : undefined;
